import { View } from "react-native";
import { AiTutor } from "../../../../graphql/generated/graphql";
import { ListItem } from "@rneui/themed";
import CustomText from "../../../common/general/CustomText/CustomText";
import { MotiView } from "moti";
import { CaretDownIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Control, Controller } from "react-hook-form";
import { Checkbox } from "react-native-paper";
import { StyleSheet } from "react-native";

interface AssessmentFormAccordionProps {
  title: string;
  aiTutors: AiTutor[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  isOpen?: boolean;
  onToggle?: () => void;
}

const AssessmentFormAccordion = ({ title, aiTutors, control, isOpen, onToggle }: AssessmentFormAccordionProps) => {
  const [internalExpanded, setInternalExpanded] = useState(true);
  const isAccordionExpanded = typeof isOpen === "boolean" ? isOpen : internalExpanded;
  const handleToggle = onToggle ? onToggle : () => setInternalExpanded((prev) => !prev);
  return (
    <View style={styles.accordionContainer}>
      <ListItem.Accordion
        noIcon={true}
        containerStyle={styles.accordion}
        content={
          <View
            style={[
              styles.accordionHeader,
              !isAccordionExpanded && {
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
              },
            ]}>
            <CustomText
              text={title}
              size="m"
              weight="ultraBold"
              useTranslationText={false}
              style={styles.accordionTitle}
            />
            <MotiView
              from={{ rotate: "0deg" }}
              animate={{ rotate: isAccordionExpanded ? "180deg" : "0deg" }}
              // @ts-expect-error - MotiView transition props have complex typing that's incompatible with simple objects
              transition={{ type: "timing", duration: 400 }}
              style={styles.accordionIcon}>
              <CaretDownIcon fill="#343330" width={20} height={20} style={{ marginTop: 2 }} weight="bold" />
            </MotiView>
          </View>
        }
        isExpanded={isAccordionExpanded}
        onPress={handleToggle}
        role="button"
        aria-checked={isAccordionExpanded}
        aria-label={`Accordion, ${isAccordionExpanded ? "expanded" : "collapsed"}`}
        accessibilityHint="Double tap to toggle the accordion.">
        <MotiView
          from={{ opacity: 0, translateY: -20 }}
          animate={{ opacity: 1, translateY: 0 }}
          // @ts-expect-error - MotiView transition props have complex typing that's incompatible with simple objects
          transition={{ type: "timing", duration: 100 }}>
          <View style={styles.aiTutorsList}>
            {aiTutors?.map((aiTutor) => (
              <Controller
                key={aiTutor.id}
                name="selectedAiTutors"
                control={control}
                render={({ field: { value, onChange } }) => {
                  const isChecked = (value || []).includes(aiTutor.id);
                  return (
                    <View style={styles.aiTutorItem}>
                      <Checkbox
                        status={isChecked ? "checked" : "unchecked"}
                        onPress={() => {
                          const currentValue = value || [];
                          if (currentValue.includes(aiTutor.id)) {
                            onChange(currentValue.filter((id) => id !== aiTutor.id));
                          } else {
                            onChange([...currentValue, aiTutor.id]);
                          }
                        }}
                        color="#66C61C"
                        uncheckedColor="#D5D7DA"
                        theme={{
                          roundness: 6,
                        }}
                      />
                      <CustomText text={aiTutor.title} useTranslationText={false} style={styles.aiTutorTitle} />
                    </View>
                  );
                }}
              />
            ))}
          </View>
        </MotiView>
      </ListItem.Accordion>
    </View>
  );
};

const styles = StyleSheet.create({
  accordionContainer: {
    marginTop: 12,
  },
  accordion: {
    backgroundColor: "transparent",
    padding: 0,
    margin: 0,
  },
  accordionHeader: {
    backgroundColor: "#F2F4F7",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 0,
  },
  accordionTitle: {
    color: "#344054",
    flex: 1,
  },
  aiTutorsList: {
    padding: 0,
    marginTop: 0,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E4E7EC",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderLeftWidth: 1,
    borderLeftColor: "#E4E7EC",
    borderRightWidth: 1,
    borderRightColor: "#E4E7EC",
    gap: 0,
  },
  aiTutorItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    marginBottom: 0,
    padding: 0,
    paddingBottom: 0,
  },
  aiTutorTitle: {
    color: "#414651",
  },
  accordionIcon: {
    marginLeft: 10,
  },
});

export default AssessmentFormAccordion;
